import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    console.log('[product-price] updatePrice called', {
      hasNewProduct: !!event.detail.data.newProduct,
      newProductId: event.detail.data.newProduct?.id,
      currentProductId: this.dataset.productId,
      hasHtml: !!event.detail.data.html,
    });

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      console.log('[product-price] Updated productId to:', this.dataset.productId);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      console.log('[product-price] Skipping - different productId', {
        targetProductId: event.target.dataset.productId,
        currentProductId: this.dataset.productId,
      });
      return;
    }

    const currentPrice = this.querySelector('[ref="priceContainer"]');
    console.log('[product-price] Current price element:', {
      exists: !!currentPrice,
      innerHTML: currentPrice?.innerHTML?.substring(0, 200),
      textContent: currentPrice?.textContent?.trim(),
    });

    if (!currentPrice) {
      console.warn('[product-price] No current price container found!');
      return;
    }

    // Check if current price already has content
    const currentPriceContent = currentPrice.textContent?.trim() || currentPrice.innerHTML?.trim();
    const hasCurrentContent = !!currentPriceContent && currentPriceContent !== '<span class="price"></span>';
    console.log('[product-price] Current price content:', {
      hasContent: hasCurrentContent,
      content: currentPriceContent?.substring(0, 100),
    });

    // First, try to get price from the morphed DOM (most reliable after morph)
    const domPrice = document.querySelector('product-price [ref="priceContainer"]');
    console.log('[product-price] Price from current DOM:', {
      exists: !!domPrice,
      isSame: domPrice === currentPrice,
      innerHTML: domPrice && domPrice !== currentPrice ? domPrice.innerHTML?.substring(0, 200) : 'same element',
      textContent: domPrice && domPrice !== currentPrice ? domPrice.textContent?.trim() : 'same element',
    });

    // Try event HTML second
    const newPrice = event.detail.data.html?.querySelector('product-price [ref="priceContainer"]');
    console.log('[product-price] New price from event HTML:', {
      exists: !!newPrice,
      innerHTML: newPrice?.innerHTML?.substring(0, 200),
      textContent: newPrice?.textContent?.trim(),
    });

    // Helper function to check if price has meaningful content
    const hasMeaningfulContent = (priceElement) => {
      if (!priceElement) return false;
      const text = priceElement.textContent?.trim() || '';
      const html = priceElement.innerHTML?.trim() || '';
      
      // Check if it has actual text content (not just whitespace or empty spans)
      if (text && text.length > 0) return true;
      
      // Check if HTML has price-related content (not just empty spans)
      const hasPriceText = html.includes('$') || html.includes('price') || html.match(/\d+/);
      const isEmptySpan = html === '<span class="price"></span>' || 
                         html === '<span role="group"></span>' ||
                         html.replace(/\s/g, '') === '<spanrole="group"></span>';
      
      return hasPriceText && !isEmptySpan;
    };

    // Priority: 1. Current price (if it has content), 2. DOM price (from morph), 3. Event HTML price
    let priceToUse = null;
    let priceSource = 'none';

    // First, check if current price already has good content - preserve it!
    if (hasCurrentContent && hasMeaningfulContent(currentPrice)) {
      console.log('[product-price] Current price has meaningful content, preserving it');
      return; // Don't update if current price is good
    }

    // Check DOM price (this is what morph updated)
    if (domPrice && domPrice !== currentPrice && hasMeaningfulContent(domPrice)) {
      priceToUse = domPrice;
      priceSource = 'DOM';
      console.log('[product-price] Using price from DOM (morphed)');
    }
    // If DOM price not available, try event HTML (only if it has meaningful content)
    else if (newPrice && hasMeaningfulContent(newPrice)) {
      priceToUse = newPrice;
      priceSource = 'event HTML';
      console.log('[product-price] Using price from event HTML');
    }

    // If we have a price to use, update
    if (priceToUse) {
      const priceContent = priceToUse.textContent?.trim() || priceToUse.innerHTML?.trim();
      const currentContent = currentPrice.textContent?.trim() || currentPrice.innerHTML?.trim();
      
      // Only update if the new price is different AND has meaningful content
      if (priceContent !== currentContent && hasMeaningfulContent(priceToUse)) {
        console.log('[product-price] Updating price from', priceSource, {
          oldContent: currentContent?.substring(0, 100),
          newContent: priceContent?.substring(0, 100),
        });
        currentPrice.replaceWith(priceToUse.cloneNode(true));
        console.log('[product-price] Price updated successfully');
      } else {
        console.log('[product-price] Price content unchanged or empty, skipping update');
      }
    } else {
      // No valid price found - preserve current if it has content
      if (hasCurrentContent) {
        console.log('[product-price] Preserving current price - no valid new price found (event HTML is empty)');
      } else {
        console.warn('[product-price] No price found anywhere! DOM:', !!domPrice, 'Event HTML:', !!newPrice);
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
